<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Stores
		import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	//Helpers
	import {
		deviceSettingFormatter,
		handleCollapseTitleLabel,
	} from 'src/helpers/device_settings_helpers';
	import { formatSnakeCaseToTitle } from '../../helpers/utilities';
	import { real_id } from '../../helpers/interface_helpers';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	//Component
	import DeviceSettingsItem from './DeviceSettingsItem.svelte';
	import { getXT1DeviceSettings } from 'src/helpers/xt1_helpers/template_settings_helpers';
	//Design system components
	import { Collapse, Container } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../types/Device';
	//Svelte hooks
	import { onMount, tick } from 'svelte';

	$: statusStore = getDeviceStatus(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	export let components = [],
		tab = '',
		device: Device,
		shouldUpdateFWVersion = false;

	let open = null;
	let collapseRefs: { [key: number]: HTMLDivElement } = {};

	let settings = deviceSettingFormatter(
		device,
		Object.values(components),
		$settingsStore,
		$statusStore,
		tab,
		false,
	);

	const isDeviceInfo = /sub_settings_title_device_information/.test(settings[0]?.title);

	const isTempFavPositions = /sub_settings_title_temperature_favorite_positions/.test(
		settings[0]?.title,
	);

	const isPinLock = /sub_settings_title_pin_lock/.test(settings[0]?.title);

	const title = (item) => {
		switch (item.type) {
			case 'i18n_string_replace':
				//values contains props for translation
				let _obj = {};
				for (let k in item.values) {
					const v = item.values[k];
					_obj[k] = $format(v) || v;
				}
				return $format(item.title, {
					default: item.titleDefault,
					values: _obj,
				});

			case 'i18n_num_replace':
				return $format(item.title, {
					default: item.titleDefault,
					values: item.values,
				});

			default:
				return $format(item.title, { default: item.titleDefault });
		}
	};

	const openCollapse = (i) => {
		i === open ? (open = null) : (open = i);
	};

	onMount(async () => {
		let targetIndex = -1;
		if ($routeToDeviceSettings?.[device.id]?.subSetting) {
			//routing to setting's tab passed from notification list and opening no matter which setting
			const subSetting = $routeToDeviceSettings[device.id].subSetting;
			targetIndex = settings.findIndex((setting) => setting.titleDefault === subSetting);
			if (targetIndex >= 0) {
				open = targetIndex;
			}
		} else if (shouldUpdateFWVersion) {
			// Auto-expanded by the `expanded` prop; still scroll the user to it.
			targetIndex = settings.findIndex((setting) => setting.titleDefault === 'firmware_version');
		}

		if (targetIndex >= 0) {
			// Wait for the Collapse to render in its expanded state before scrolling
			// so the target sits at the top of the visible area instead of below the
			// fold once the panel grows.
			await tick();
			collapseRefs[targetIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}

		if (Object.keys($routeToDeviceSettings || {}).length > 0) {
			//cleaning route after opening desired setting
			routeToDeviceSettings.resetTarget();
		}
	});

	const XT1DeviceSettings =
		tab == 'settings' && isXT1Module(device?.type) ? getXT1DeviceSettings(device) : null;
</script>

{#if tab !== 'application_settings' && settings?.length == 1}
	{#if isDeviceInfo || isTempFavPositions}
		<Container class="border-color-transparent" type="modalContainer" padding="xs">
			<DeviceSettingsItem {device} setting={settings[0]} />
		</Container>
	{:else if !isPinLock}
		<DeviceSettingsItem {device} setting={settings[0]} />
	{:else}
		<Collapse
			id="collapse-w3h61ed"
			title={formatSnakeCaseToTitle(
				$format(settings[0].title, { default: settings[0].titleDefault }),
			)}
			expanded={open !== 0}
			class="mb-s collapse-title-left-align"
			on:click={() => openCollapse(0)}
		>
			<DeviceSettingsItem {device} setting={settings[0]} />
		</Collapse>
	{/if}
{:else if tab === 'application_settings' || settings?.length > 1}
	{#if XT1DeviceSettings}
		<svelte:component
			this={XT1DeviceSettings}
			{device}
			startOpenIndex={settings.length}
			bind:openIndex={open}
		/>
	{/if}
	{#each settings as setting, i}
		<div bind:this={collapseRefs[i]} class="device-settings-wrapper__collapse">
			<Collapse
				id="collapse-fxeq3z9"
				class="mb-s collapse-title-left-align"
				title={formatSnakeCaseToTitle(title(setting))}
				expanded={open === i ||
					(shouldUpdateFWVersion && setting.titleDefault == 'firmware_version')}
				on:click={() => openCollapse(i)}
				label={handleCollapseTitleLabel(setting)}
			>
				<DeviceSettingsItem {device} {setting} />
			</Collapse>
		</div>
	{/each}
{/if}

<style>
	/* Fix for BTGG-230: Force left-align for collapse titles in TRV/GW settings */
	:global(.collapse-title-left-align .collapse-header) {
		text-align: left !important;
	}
</style>
